import { BaPersonInfo } from 'Shared/types/BaInfo.types';
import React from 'react';
import BehavioralPatternBar, { BASigmaName, BehavioralPatternBarProps } from './BehavioralPatternBar';
import './BehavioralPatternGraph.scss';
import { BAFactorShapeType, BehavioralPatternGraphType, behavioralPatternGraphs } from './FactorShape.service';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  baPersonInfo: BaPersonInfo;
  /**
   * If provided, it will cause the graph to be a combined graph, and the
   * shapeType is ignored, as the first sigma value will always be displayed
   * as a square, and the second will be a circle.
   */
  otherBAPersonInfo?: BaPersonInfo;
  graphType: BehavioralPatternGraphType;
  /**
   * Determines if a connecting line should be shown between the sigmas on
   * the bars.
   */
  useConnectingLine?: boolean;
  /**
   * Determines if the names on the sides of the bars should be shown. For
   * example the A sigma would have `Collaborative` on the left and
   * `Independent` on the right.
   *
   * This text will automatically be hidden if the container is too small.
   */
  useBarDescriptions?: boolean;
  /**
   * The shape type of the graph. If not provided, it will default to a blue
   * circle. This is ignored if otherBAPersonInfo is provided.
   */
  shapeType?: BAFactorShapeType;
};

const BehavioralPatternGraph = ({
  baPersonInfo,
  otherBAPersonInfo,
  graphType,
  useConnectingLine,
  useBarDescriptions,
  shapeType,
}: Props): React.JSX.Element => {
  const { tr } = useTranslation();
  const baseClassName = 'BehavioralPatternGraph';
  const graphInfo = behavioralPatternGraphs[graphType];

  const sigmas: Partial<BehavioralPatternBarProps>[] = [
    {
      sigmaValue: baPersonInfo.selfAssessmentScore.aSigma,
      secondSigmaValue: otherBAPersonInfo?.selfAssessmentScore.aSigma,
      sigmaName: BASigmaName.A,
    },
    {
      sigmaValue: baPersonInfo.selfAssessmentScore.bSigma,
      secondSigmaValue: otherBAPersonInfo?.selfAssessmentScore.bSigma,
      sigmaName: BASigmaName.B,
      connectingLineInfo: useConnectingLine
        ? {
            previousSigmaValue: baPersonInfo.selfAssessmentScore.aSigma,
            secondPreviousSigmaValue: otherBAPersonInfo?.selfAssessmentScore.aSigma,
          }
        : undefined,
    },
    {
      sigmaValue: baPersonInfo.selfAssessmentScore.cSigma,
      secondSigmaValue: otherBAPersonInfo?.selfAssessmentScore.cSigma,
      sigmaName: BASigmaName.C,
      connectingLineInfo: useConnectingLine
        ? {
            previousSigmaValue: baPersonInfo.selfAssessmentScore.bSigma,
            secondPreviousSigmaValue: otherBAPersonInfo?.selfAssessmentScore.bSigma,
          }
        : undefined,
    },
    {
      sigmaValue: baPersonInfo.selfAssessmentScore.dSigma,
      secondSigmaValue: otherBAPersonInfo?.selfAssessmentScore.dSigma,
      sigmaName: BASigmaName.D,
      connectingLineInfo: useConnectingLine
        ? {
            previousSigmaValue: baPersonInfo.selfAssessmentScore.cSigma,
            secondPreviousSigmaValue: otherBAPersonInfo?.selfAssessmentScore.cSigma,
          }
        : undefined,
    },
    {
      sigmaValue: baPersonInfo.selfAssessmentScore.eSigma,
      secondSigmaValue: otherBAPersonInfo?.selfAssessmentScore.eSigma,
      sigmaName: BASigmaName.E,
    },
  ];

  function getSigmaDescription(side: 'left' | 'right', sigmaName: BASigmaName) {
    switch (sigmaName) {
      case BASigmaName.A:
        return side === 'left'
          ? tr('perform.behavioral_pattern_graph.sigma_a_left')
          : tr('perform.behavioral_pattern_graph.sigma_a_right');
      case BASigmaName.B:
        return side === 'left'
          ? tr('perform.behavioral_pattern_graph.sigma_b_left')
          : tr('perform.behavioral_pattern_graph.sigma_b_right');
      case BASigmaName.C:
        return side === 'left'
          ? tr('perform.behavioral_pattern_graph.sigma_c_left')
          : tr('perform.behavioral_pattern_graph.sigma_c_right');
      case BASigmaName.D:
        return side === 'left'
          ? tr('perform.behavioral_pattern_graph.sigma_d_left')
          : tr('perform.behavioral_pattern_graph.sigma_d_right');
      case BASigmaName.E:
        return side === 'left'
          ? tr('perform.behavioral_pattern_graph.sigma_e_left')
          : tr('perform.behavioral_pattern_graph.sigma_e_right');
    }
  }

  const barSpacing = behavioralPatternGraphs[graphType].spacing;
  const barHeight = behavioralPatternGraphs[graphType].height;
  const barWidth = behavioralPatternGraphs[graphType].width;

  const sigmaLegendLines = Array.from({ length: 31 }).map((_, index) => {
    const line = (
      <line
        x1={(index * (barWidth - barHeight)) / 30 + barHeight / 2}
        y1={0}
        x2={(index * (barWidth - barHeight)) / 30 + barHeight / 2}
        y2={index % 5 === 0 ? 6 : 4}
        className={`${baseClassName}__sigma-marker`}
      />
    );
    if (index % 5 === 0) {
      return (
        <>
          {line}
          <text
            x={(index * (barWidth - barHeight)) / 30 + barHeight / 2}
            y={20}
            className={`${baseClassName}__sigma-legend`}
          >
            {(index > 15 ? '+' : '') + (index / 5 - 3) + (index === 15 ? '' : '\u03C3')}
          </text>
        </>
      );
    }
    return line;
  });

  const sigmaLegend =
    graphType === BehavioralPatternGraphType.Mini ? null : (
      <svg xmlns="http://www.w3.org/2000/svg" className={`${baseClassName}__sigma-legend-svg`}>
        {sigmaLegendLines}
      </svg>
    );

  return (
    <div className={`${baseClassName} ${useBarDescriptions ? `${baseClassName}--has-text` : ''}`}>
      <div className={useBarDescriptions ? `${baseClassName}--has-text__grid` : ''}>
        {useBarDescriptions && (
          <div
            className={`${baseClassName}__text-column ${baseClassName}__text-column-left`}
            style={{ gap: `${barSpacing}px` }}
          >
            {sigmas.map((factorInfo, index) => (
              <span style={{ maxHeight: `${graphInfo.height}px` }} key={index}>
                {getSigmaDescription('left', factorInfo.sigmaName)}
              </span>
            ))}
          </div>
        )}

        <div
          className={`${baseClassName}__graph-bar-column`}
          style={{ gap: `${barSpacing}px`, marginTop: `${barSpacing}px` }}
        >
          {sigmas.map((factorInfo, index) => (
            <BehavioralPatternBar
              sigmaValue={factorInfo.sigmaValue}
              secondSigmaValue={factorInfo.secondSigmaValue}
              key={index}
              graphType={graphType}
              sigmaName={factorInfo.sigmaName}
              connectingLineInfo={factorInfo.connectingLineInfo}
              shapeType={shapeType}
            />
          ))}
          {sigmaLegend}
        </div>

        {useBarDescriptions && (
          <div
            className={`${baseClassName}__text-column ${baseClassName}__text-column-right`}
            style={{ gap: `${barSpacing}px` }}
          >
            {sigmas.map((factorInfo, index) => (
              <span style={{ maxHeight: `${graphInfo.height}px` }} key={index}>
                {getSigmaDescription('right', factorInfo.sigmaName)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BehavioralPatternGraph;
